import { useCallback, useState } from "react";

import { Modal, Typography } from "@king-ict/eupisi-ui/components";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "../../../app/store";
import { updateRequestErrors } from "../../../slices/requestSlice";

// Save request is performed via fetch (not global Axios) so the UI library's
// ResponseHandler does not show a second error modal behind this one.
// 413 = Payload Too Large (e.g. from proxy/gateway). 503 = workaround when
// server returns 503 for body too large because the backend never sees the request.
// 0 = network error (e.g. Firefox "NetworkError when attempting to fetch resource" on large payload).
const HTTP_STATUS_PAYLOAD_TOO_LARGE = 413;
const HTTP_STATUS_SERVICE_UNAVAILABLE_AS_FILE_TOO_LARGE = 503;
const FILE_TOO_LARGE_STATUSES = [
  0,
  HTTP_STATUS_PAYLOAD_TOO_LARGE,
  HTTP_STATUS_SERVICE_UNAVAILABLE_AS_FILE_TOO_LARGE,
];

export const useSaveRequestError = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isFileSizeTooLargeModalOpen, setIsFileSizeTooLargeModalOpen] =
    useState(false);

  const handleSaveRequestError = useCallback(
    (error: unknown) => {
      const status = (error as { status?: number })?.status;
      if (status !== undefined && FILE_TOO_LARGE_STATUSES.includes(status)) {
        setIsFileSizeTooLargeModalOpen(true);
      } else {
        const message =
          (error as { message?: string })?.message ?? "Request failed";
        dispatch(updateRequestErrors([{ propertyName: "", message }]));
      }
    },
    [dispatch]
  );

  const fileSizeTooLargeModal = (
    <Modal
      open={isFileSizeTooLargeModalOpen}
      title={t("common.error")}
      type="error"
      confirmText={t("common.close")}
      onClose={() => setIsFileSizeTooLargeModalOpen(false)}
      onConfirm={() => setIsFileSizeTooLargeModalOpen(false)}
      variant="big"
    >
      <Typography component="div" variant="medium">
        {t("request.fileSizeTooLarge413")}
      </Typography>
    </Modal>
  );

  return { handleSaveRequestError, fileSizeTooLargeModal };
};
